import { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Container,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Box,
    Paper,
    Avatar,
    TextField,
    InputAdornment
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Refresh, FileDownload } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import services from 'src/services/Services';
import InstanceDataUser from 'src/services/InstanceDataUser';
import WidgetCustomLoader from 'src/widgets/WidgetCustomLoader';

const Existencias = () => {
    const [dataProducts, setDataProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [legend, setLegend] = useState('');
    const [report, setReport] = useState(2);
    const [searchText, setSearchText] = useState('');

    const filteredRows=useMemo(() => {
return dataProducts.filter((row) =>
Object.values(row).some((value) =>
String(value).toLowerCase().includes(searchText.toLowerCase())
    )
  );
}, [dataProducts, searchText]);
 

const renderBullets = (html) => {
        if (!html) return null;
 
        const items = html
            .replace(/<\/?ul>/g, '')
            .split('</li>')
            .map(item => item.replace('<li>', '').trim())
            .filter(Boolean);
 
        return (
<Box
                sx={{
                    maxHeight: 80,          // 👈 altura máxima de la celda
                    overflowY: "auto",      // 👈 scroll vertical
                    pr: 1,                  // espacio para scrollbar
                }}
>
<ul style={{ paddingLeft: 16, margin: 0 }}>
                {items.map((item, index) => (
<li key={index} style={{ fontSize: 12, lineHeight: 1.4 }}>
                        {item}
</li>
                ))}
</ul>
</Box>
        );
    };
 
    const columnsFull = [
        { field: 'Articulo', headerName: 'Artículo', width: 100 },
        { field: 'Descripcion', headerName: 'Descripción', width: 400 },
        { field: 'Precio', headerName: 'Precio', width: 90 },
        { field: 'Moneda', headerName: 'Moneda', width: 100 },
        { field: 'TipoCambio', headerName: 'Tipo Cambio', width: 125 },
        { field: 'Disponible', headerName: 'Disponible', width: 130 },
        { field: 'Almacen', headerName: 'Almacén', width: 100 },
        { field: 'Grupo', headerName: 'Grupo', width: 100 },
        { field: 'SeCompraICM', headerName: 'Se Compra ICM', width: 100 },
        { field: 'EstatusICM', headerName: 'Estatus ICM', width: 100 },
        { field: 'UPC', headerName: 'UPC', width: 100 },
        {
            field: 'Caracteristicas',
            headerName: 'Características',
            minWidth: 300,
            flex: 1,
            renderCell: (params) => renderBullets(params.value)
        },
        {
            field: 'Especificaciones',
            headerName: 'Especificaciones',
            minWidth: 300,
            flex: 1,
            renderCell: (params) => renderBullets(params.value)
        },
        { field: 'Dimensiones', headerName: 'Dimensiones', width: 100 },
        { field: 'Peso', headerName: 'Peso', width: 100 },
        {
            field: 'FichaTecnica',
            headerName: 'Ficha Técnica',
            minWidth: 300,
            flex: 1,
            sortable: true,
            renderCell: (params) => {
                if (!params.value) return '—';
 
                return (
<a
                        href={params.value}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            color: '#1976d2',
                            textDecoration: 'underline',
                            wordBreak: 'break-all'
                        }}
>
                        Ficha Técnica
</a>
                );
            }
        },
        {
            field: 'Imagenes',
            headerName: 'Imágenes',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (!params.value) {
                    return '—';
                }
 
                return (
<img
                        src={params.value}
                        alt="no disponible"
                        width={100}
                        height={80}
                        style={{
                            objectFit: 'contain',
                            borderRadius: 4
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/no-image.png'; // opcional
                        }}
                    />
                );
            }
        },
        { field: 'ProductName', headerName: 'Product Name', width: 200 },
        { field: 'Sku', headerName: 'Sku', width: 100 },
        { field: 'Almacen', headerName: 'Almacen', width: 100 },
        { field: 'Familia', headerName: 'Familia', width: 100 },
    ];
 
    const columnsBasic = [
        { field: 'Articulo', headerName: 'Artículo', width: 100 },
        { field: 'Descripcion', headerName: 'Descripción', width: 400 },
        { field: 'Precio', headerName: 'Precio', width: 90, hide: InstanceDataUser.getPrice() },
        { field: 'Moneda', headerName: 'Moneda', width: 100 },
        { field: 'TipoCambio', headerName: 'Tipo Cambio', width: 125 },
        { field: 'Disponible', headerName: 'Disponible', width: 130 },
        { field: 'Almacen', headerName: 'Almacén', width: 100 },
        { field: 'Grupo', headerName: 'Grupo', width: 100 },
        { field: 'SeCompraICM', headerName: 'Se Compra ICM', width: 100 },
        { field: 'EstatusICM', headerName: 'Estatus ICM', width: 100 }
    ];

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await services.getAllProducts();
            const legendText = 'Inventario actualizado ' + response.data.Fecha;
            const products = response.data.Products.map(product => ({
                id: product.UPC,
                ...product,
                Imagenes: Array.isArray(product.Imagenes) && product.Imagenes[0]
            }));
            console.log(products.length);
            setDataProducts(products);
            setLegend(legendText);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleReportChange = (event, val) => {
        if (val !== null) setReport(val);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper elevation={0} sx={{ mb: 3, p: 2.5, borderRadius: 3, background: 'linear-gradient(180deg,#fff,#fbfdff)' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'transparent', width: 56, height: 56 }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 3h18v3H3z" fill="#556cd6"/>
                                <path d="M3 8h18v12H3z" stroke="#1976d2" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Existencias</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{legend}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {!loading && (
                            <>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<Refresh />}
                                    onClick={fetchProducts}
                                    sx={{
                                        borderRadius: 3,
                                        px: 2.5,
                                        py: 0.7,
                                        background: 'linear-gradient(120deg,#556cd6, #4dd0e1)',
                                        color: '#fff',
                                        fontWeight: 700,
                                        boxShadow: '0 8px 26px rgba(85,108,214,0.12)',
                                        textTransform: 'none',
                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 14px 40px rgba(85,108,214,0.16)' }
                                    }}
                                >
                                    Actualizar Inventario
                                </Button>

                                {!InstanceDataUser.getExport && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<FileDownload />}
                                        sx={{ borderRadius: 3, px: 2, py: 0.7, textTransform: 'none' }}
                                    >
                                        Exportar CSV
                                    </Button>
                                )}

                                <ToggleButtonGroup
                                    size="small"
                                    value={report}
                                    exclusive
                                    onChange={handleReportChange}
                                    sx={{ ml: 1, borderRadius: 3 }}
                                >
                                    <ToggleButton value={1} sx={{ textTransform: 'none' }}>Completo</ToggleButton>
                                    <ToggleButton value={2} sx={{ textTransform: 'none' }}>Básico</ToggleButton>
                                </ToggleButtonGroup>
                            </>
                        )}
                    </Box>
                </Box>
                
            </Paper>

            <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 12px 40px rgba(16,24,40,0.06)' }}>
                <Box sx={{ width: '100%', '& .MuiDataGrid-root': { border: 'none' } }}>
                    <TextField

                    size="small"

                    placeholder="Buscar…"

                    value={searchText}

                    onChange={(e) => setSearchText(e.target.value)}

                    InputProps={{

                        startAdornment: (
<InputAdornment position="start">
<SearchIcon />
</InputAdornment>

                        ),

                    }}

                />
 
                    <DataGrid
                        rows={filteredRows}
                        columns={report === 2 ? columnsBasic : columnsFull}
                        columnVisibilityModel={{
                            Precio: !InstanceDataUser.getPrice()
                        }}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        checkboxSelection
                        loading={loading}
                        getRowId={(row) => row.Articulo}
                        components={{
                            Toolbar: GridToolbar,
                            LoadingOverlay: WidgetCustomLoader
                        }}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f3f7fb', fontWeight: 800, borderBottom: '1px solid rgba(0,0,0,0.04)' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#fbfdff', transform: 'translateY(-2px)' },
                            '& .MuiDataGrid-cell': { alignItems: 'center' },
                            '& .MuiDataGrid-footerContainer': { backgroundColor: '#fafcff', borderTop: '1px solid rgba(0,0,0,0.04)' },
                            '& .MuiDataGrid-virtualScroller': { background: 'linear-gradient(180deg,#fff,#fbfdff)' },
                            '& .MuiDataGrid-toolbarContainer': { px: 2 },
                        }}
                        autoHeight
                    />
                </Box>
            </Paper>
        </Container>
    );
};

export default Existencias;
